// Typed M18 Internal Chat client (API §16c). Responses are camelCase by contract.
import { apiRequest, apiRequestEnvelope, apiUrl } from "./apiClient";

export type ChatConversationType =
  | "DIRECT"
  | "GROUP"
  | "DEPARTMENT"
  | "ANNOUNCEMENT";

export interface ChatMember {
  id: string;
  fullName: string;
}

export interface ChatAttachment {
  fileId: string;
  name: string;
  sizeBytes: number;
  mimeType: string | null;
  quarantined: boolean;
}

export interface ChatTaskRef {
  entityType: "TASK";
  entityId: string;
  canAccess: boolean;
  title: string | null;
  status: string | null;
}

export interface ChatReaction {
  emoji: string;
  count: number;
  my: boolean;
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  authorId: string | null;
  authorName: string | null;
  body: string;
  replyToId: string | null;
  replyTo: { id: string; authorName: string | null; bodyPreview: string } | null;
  createdAt: string;
  editedAt: string | null;
  deletedAt: string | null;
  reactions: ChatReaction[];
  attachments: ChatAttachment[];
  refs: ChatTaskRef[];
}

export interface ChatLastMessage {
  id: string;
  authorId: string | null;
  authorName: string | null;
  bodyPreview: string;
  createdAt: string;
}

export interface ChatConversation {
  id: string;
  type: ChatConversationType;
  title: string;
  departmentId: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  members: ChatMember[];
  lastMessage: ChatLastMessage | null;
  unreadCount: number;
  muted: boolean;
  myRole: "OWNER" | "MEMBER" | null;
  lastReadMessageId: string | null;
}

export interface ChatUnreadSummary {
  totalUnread: number;
  conversations: Array<{ id: string; unreadCount: number }>;
}

function e(value: string): string {
  return encodeURIComponent(value);
}

export const chatApi = {
  conversations: async (archived = false): Promise<ChatConversation[]> => {
    const { data } = await apiRequestEnvelope<{
      conversations: ChatConversation[];
    }>(`/api/chat/conversations${archived ? "?archived=true" : ""}`);
    return data.conversations;
  },
  conversation: async (id: string): Promise<ChatConversation> => {
    const { data } = await apiRequestEnvelope<{ conversation: ChatConversation }>(
      `/api/chat/conversations/${e(id)}`,
    );
    return data.conversation;
  },
  direct: async (userId: string): Promise<ChatConversation> => {
    const { data } = await apiRequestEnvelope<{ conversation: ChatConversation }>(
      "/api/chat/direct",
      { method: "POST", body: { userId } },
    );
    return data.conversation;
  },
  createGroup: async (title: string, memberIds: string[]): Promise<ChatConversation> => {
    const { data } = await apiRequestEnvelope<{ conversation: ChatConversation }>(
      "/api/chat/groups",
      { method: "POST", body: { title, memberIds } },
    );
    return data.conversation;
  },
  updateGroup: async (
    id: string,
    body: { title?: string; ownerId?: string },
  ): Promise<ChatConversation> => {
    const { data } = await apiRequestEnvelope<{ conversation: ChatConversation }>(
      `/api/chat/groups/${e(id)}`,
      { method: "PATCH", body },
    );
    return data.conversation;
  },
  addMembers: async (id: string, userIds: string[]): Promise<void> => {
    await apiRequest(`/api/chat/groups/${e(id)}/members`, {
      method: "POST",
      body: { userIds },
    });
  },
  removeMember: async (id: string, userId: string): Promise<void> => {
    await apiRequest(`/api/chat/groups/${e(id)}/members/${e(userId)}`, {
      method: "DELETE",
    });
  },
  leaveGroup: async (id: string): Promise<void> => {
    await apiRequest(`/api/chat/groups/${e(id)}/leave`, { method: "POST" });
  },
  archiveGroup: async (id: string, archive: boolean): Promise<ChatConversation> => {
    const { data } = await apiRequestEnvelope<{ conversation: ChatConversation }>(
      `/api/chat/groups/${e(id)}/${archive ? "archive" : "restore"}`,
      { method: "POST" },
    );
    return data.conversation;
  },
  messages: async (
    conversationId: string,
    query: { before?: string; after?: string; message?: string; limit?: number } = {},
  ): Promise<ChatMessage[]> => {
    const params = new URLSearchParams();
    if (query.before) params.set("before", query.before);
    if (query.after) params.set("after", query.after);
    if (query.message) params.set("message", query.message);
    params.set("limit", String(query.limit ?? 50));
    const { data } = await apiRequestEnvelope<{ messages: ChatMessage[] }>(
      `/api/chat/conversations/${e(conversationId)}/messages?${params.toString()}`,
    );
    return data.messages;
  },
  // W2-5 (VF-CAP-009): інкрементальний poll з ETag — незмінна розмова дає
  // 304 (null) за 1 SQL на бекенді; fetch напряму, бо apiRequestEnvelope не
  // віддає статус/заголовки. Помилки мережі кидаються — poll їх ковтає сам.
  pollMessages: async (
    conversationId: string,
    query: { after?: string; limit?: number; etag?: string | null } = {},
  ): Promise<{ messages: ChatMessage[]; etag: string | null } | null> => {
    const params = new URLSearchParams();
    if (query.after) params.set("after", query.after);
    params.set("limit", String(query.limit ?? 50));
    const headers: Record<string, string> = {};
    if (query.etag) headers["If-None-Match"] = query.etag;
    const response = await fetch(
      apiUrl(`/api/chat/conversations/${e(conversationId)}/messages?${params.toString()}`),
      { credentials: "include", headers },
    );
    if (response.status === 304) return null;
    if (!response.ok) throw new Error(`poll failed: ${response.status}`);
    const payload = (await response.json()) as { data: { messages: ChatMessage[] } };
    return { messages: payload.data.messages, etag: response.headers.get("ETag") };
  },
  send: async (
    conversationId: string,
    body: { body: string; replyToId?: string; taskRefs?: string[] },
  ): Promise<ChatMessage> => {
    const { data } = await apiRequestEnvelope<{ message: ChatMessage }>(
      `/api/chat/conversations/${e(conversationId)}/messages`,
      { method: "POST", body },
    );
    return data.message;
  },
  edit: async (messageId: string, body: string): Promise<void> => {
    await apiRequestEnvelope(`/api/chat/messages/${e(messageId)}`, {
      method: "PATCH",
      body: { body },
    });
  },
  remove: async (messageId: string): Promise<void> => {
    await apiRequest(`/api/chat/messages/${e(messageId)}`, { method: "DELETE" });
  },
  react: async (messageId: string, emoji: string): Promise<boolean> => {
    const { data } = await apiRequestEnvelope<{ added: boolean }>(
      `/api/chat/messages/${e(messageId)}/reactions`,
      { method: "POST", body: { emoji } },
    );
    return data.added;
  },
  upload: async (messageId: string, file: File): Promise<ChatAttachment> => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await apiRequestEnvelope<{ attachment: ChatAttachment }>(
      `/api/chat/messages/${e(messageId)}/attachments`,
      { method: "POST", body: form },
    );
    return data.attachment;
  },
  download: async (messageId: string, fileId: string): Promise<string | null> => {
    const { data } = await apiRequestEnvelope<{ downloadUrl: string | null }>(
      `/api/chat/messages/${e(messageId)}/attachments/${e(fileId)}/download`,
    );
    return data.downloadUrl;
  },
  previewUrl: (messageId: string, fileId: string): string =>
    `/api/chat/messages/${e(messageId)}/attachments/${e(fileId)}/preview`,
  markRead: async (conversationId: string, lastMessageId: string): Promise<boolean> => {
    const { data } = await apiRequestEnvelope<{ changed: boolean }>(
      `/api/chat/conversations/${e(conversationId)}/read`,
      { method: "POST", body: { lastMessageId } },
    );
    return data.changed;
  },
  setMuted: async (conversationId: string, muted: boolean): Promise<boolean> => {
    const { data } = await apiRequestEnvelope<{ muted: boolean }>(
      `/api/chat/conversations/${e(conversationId)}/membership`,
      { method: "PATCH", body: { muted } },
    );
    return data.muted;
  },
  unreadSummary: async (): Promise<ChatUnreadSummary> => {
    const { data } = await apiRequestEnvelope<ChatUnreadSummary>(
      "/api/chat/unread-summary",
    );
    return data;
  },
};
